<script setup>
import { computed, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import FilterSelect from 'dashboard/components-next/filter/inputs/FilterSelect.vue';
import Input from 'dashboard/components-next/input/Input.vue';
import Icon from 'next/icon/Icon.vue';

const props = defineProps({
  totalCampaigns: {
    type: Number,
    required: true,
  },
  perPage: {
    type: Number,
    default: 10,
  },
  currentPage: {
    type: Number,
    default: 1,
  },
  currentFilter: {
    type: String,
    default: 'all',
  },
  currentSort: {
    type: String,
    default: 'date-desc',
  },
  searchQuery: {
    type: String,
    default: '',
  },
});

const emit = defineEmits([
  'update:currentPage',
  'update:currentFilter',
  'update:currentSort',
  'update:searchQuery',
]);

const { t } = useI18n();

const searchInput = ref(props.searchQuery);
const timeoutId = ref(null);
const selectedFilter = defineModel('currentFilter', { default: 'all' });
const selectedSort = defineModel('currentSort', { default: 'date-desc' });

const filterOptions = [
  { value: 'all', label: t('CAMPAIGN.FILTER.ALL') },
  { value: 'active', label: t('CAMPAIGN.FILTER.ACTIVE') },
  { value: 'running', label: t('CAMPAIGN.FILTER.RUNNING') },
  { value: 'completed', label: t('CAMPAIGN.FILTER.COMPLETED') },
  { value: 'error', label: t('CAMPAIGN.FILTER.ERROR') },
];

const sortOptions = [
  { value: 'date-desc', label: t('CAMPAIGN.SORT.DATE_DESC') },
  { value: 'date-asc', label: t('CAMPAIGN.SORT.DATE_ASC') },
  { value: 'name-asc', label: t('CAMPAIGN.SORT.NAME_ASC') },
  { value: 'name-desc', label: t('CAMPAIGN.SORT.NAME_DESC') },
];

const totalPages = computed(() => {
  return Math.ceil(props.totalCampaigns / props.perPage);
});

const pageNumbers = computed(() => {
  if (totalPages.value <= 7) {
    return Array.from({ length: totalPages.value }, (_, i) => i + 1);
  }

  const current = props.currentPage;
  const pages = [];

  pages.push(1);

  if (current > 3) {
    pages.push('...');
  }

  const start = Math.max(2, current - 1);
  const end = Math.min(totalPages.value - 1, current + 1);

  for (let i = start; i <= end; i += 1) {
    pages.push(i);
  }

  if (current < totalPages.value - 2) {
    pages.push('...');
  }

  if (totalPages.value > 1) {
    pages.push(totalPages.value);
  }

  return pages;
});

const changePage = page => {
  if (page !== '...' && page >= 1 && page <= totalPages.value) {
    emit('update:currentPage', page);
  }
};

const changeFilter = filter => {
  emit('update:currentFilter', filter);
  emit('update:currentPage', 1);
};

const changeSort = sort => {
  emit('update:currentSort', sort);
};

const handleSearch = event => {
  if (timeoutId.value) {
    clearTimeout(timeoutId.value);
  }

  searchInput.value = event.target.value;

  timeoutId.value = setTimeout(() => {
    emit('update:searchQuery', searchInput.value);
    emit('update:currentPage', 1);
  }, 300);
};

const clearAllFilters = () => {
  searchInput.value = '';
  emit('update:searchQuery', '');
  selectedFilter.value = 'all';
  selectedSort.value = 'date-desc';
  emit('update:currentPage', 1);
};

const hasPrevPage = computed(() => props.currentPage > 1);

const hasNextPage = computed(() => props.currentPage < totalPages.value);

const hasActiveFilters = computed(() => {
  return (
    props.searchQuery !== '' ||
    props.currentFilter !== 'all' ||
    props.currentSort !== 'date-desc'
  );
});

watch(
  () => props.searchQuery,
  newValue => {
    searchInput.value = newValue;
  }
);

watch(
  () => props.currentFilter,
  newValue => {
    selectedFilter.value = newValue;
  }
);

watch(
  () => props.currentSort,
  newValue => {
    selectedSort.value = newValue;
  }
);

watch(selectedFilter, newValue => {
  if (newValue !== props.currentFilter) {
    changeFilter(newValue);
  }
});

watch(selectedSort, newValue => {
  if (newValue !== props.currentSort) {
    changeSort(newValue);
  }
});
</script>

<template>
  <div class="campaign-filters mb-4">
    <div class="mb-4 flex items-center space-x-3">
      <div class="flex-shrink-0 w-64">
        <Input
          v-model="searchInput"
          type="search"
          class="w-full"
          custom-input-class="h-10 ltr:!pl-9 rtl:!pr-9"
          :placeholder="t('CAMPAIGN.SEARCH.PLACEHOLDER')"
          @input="handleSearch"
        >
          <template #prefix>
            <Icon
              icon="i-lucide-search"
              class="absolute size-4 top-1/2 transform -translate-y-1/2 text-n-slate-11 ltr:left-3 rtl:right-3"
            />
          </template>
        </Input>
      </div>

      <div class="flex-shrink-0">
        <FilterSelect
          v-model="selectedFilter"
          :options="filterOptions"
          :variant="selectedFilter !== 'all' ? 'active' : 'faded'"
        />
      </div>

      <div class="flex-shrink-0">
        <FilterSelect
          v-model="selectedSort"
          :options="sortOptions"
          :variant="selectedSort !== 'date-desc' ? 'active' : 'faded'"
        />
      </div>

      <button
        v-if="hasActiveFilters"
        class="h-10 px-3 py-0 text-sm text-gray-600 border border-gray-300 rounded-md hover:bg-gray-50 flex items-center"
        @click="clearAllFilters"
      >
        <span class="flex items-center">
          <FluentIcon icon="dismiss" size="14" class="mr-1.5" />
          {{ t('CAMPAIGN.FILTER.CLEAR_ALL') }}
        </span>
      </button>
    </div>

    <div class="flex items-center justify-between text-sm text-gray-500 mb-4">
      <span>
        {{
          t('CAMPAIGN.PAGINATION.SHOWING', {
            from: totalCampaigns ? (currentPage - 1) * perPage + 1 : 0,
            to: Math.min(currentPage * perPage, totalCampaigns),
            total: totalCampaigns,
          })
        }}
      </span>

      <div v-if="totalPages > 1" class="flex items-center">
        <button
          class="px-2 py-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          :disabled="!hasPrevPage"
          @click="changePage(currentPage - 1)"
        >
          <FluentIcon icon="chevron-left" size="16" />
        </button>

        <button
          v-for="page in pageNumbers"
          :key="page"
          class="px-3 py-1 rounded-md flex items-center justify-center"
          :class="
            page === currentPage
              ? 'bg-blue-500 text-white'
              : 'hover:bg-gray-100'
          "
          :disabled="page === '...'"
          @click="changePage(page)"
        >
          {{ page }}
        </button>

        <button
          class="px-2 py-1 rounded-md hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
          :disabled="!hasNextPage"
          @click="changePage(currentPage + 1)"
        >
          <FluentIcon icon="chevron-right" size="16" />
        </button>
      </div>
    </div>
  </div>
</template>
